'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import { pickChrome } from '../../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName } from '../MenuItemTags';
import { isMenuItemActive, resolveItemImageUrl } from '../menuEditHelpers';
import { readHighlightMenuSection } from './highlightMenuItems';

type Plate = ReturnType<typeof readHighlightMenuSection>['dishes'][number];

function LeadPlate({
  plate,
  block,
  locale,
  editable,
  activeEditTarget,
  interactive,
  plateIndex,
  selected,
  tabPanelId,
}: {
  plate: Plate;
  block: BlockRenderProps['block'];
  locale: string;
  editable?: boolean;
  activeEditTarget: BlockRenderProps['activeEditTarget'];
  interactive: boolean;
  plateIndex: number;
  selected: boolean;
  tabPanelId?: string;
}) {
  const target = activeEditTarget;
  const imageUrl = resolveItemImageUrl(
    block,
    plate.item,
    plate.columnIndex,
    plate.itemIndex,
  );
  const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 1200, 78) : undefined;
  const imageSrcSet = imageUrl
    ? buildSiteImageSrcSet(imageUrl, [480, 720, 960, 1200], 78)
    : undefined;

  return (
    <article
      className={[
        'ws-hl-hero__lead',
        selected ? 'ws-hl-hero__lead--active' : '',
        isMenuItemActive(
          target?.kind === 'menu-item' ? target : null,
          plate.columnIndex,
          plate.itemIndex,
        )
          ? 'ws-edit-child--active'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-hero-index={interactive ? String(plateIndex) : undefined}
      role={interactive ? 'tabpanel' : undefined}
      aria-labelledby={interactive && selected ? tabPanelId : undefined}
      aria-hidden={interactive && !selected ? true : undefined}
      {...(editable
        ? {
            'data-edit-child': 'menu-item' as const,
            'data-column-index': plate.columnIndex,
            'data-item-index': plate.itemIndex,
          }
        : {})}
    >
      <div className="ws-hl-hero__media">
        {imageUrl ? (
          <EditableMedia
            fieldKey={`itemImage_${plate.columnIndex}_${plate.itemIndex}`}
            editable={editable}
            activeEditTarget={activeEditTarget}
            className="ws-hl-hero__media-frame"
            src={imageSrc}
            srcSet={imageSrcSet}
            sizes={SITE_IMAGE_SIZES.menuHighlightHero}
            alt={plate.item.name || 'Dish'}
            width={1200}
            height={900}
            loading="eager"
            decoding="async"
          />
        ) : (
          <div className="ws-hl-hero__media-fallback" aria-hidden />
        )}
      </div>
      <div className="ws-hl-hero__body">
        <div className="ws-hl-hero__head">
          <MenuItemName
            item={plate.item}
            locale={locale}
            className="ws-hl-hero__name"
            showLegalMarks={false}
            showOrderCode={false}
          />
          <MenuItemDietaryTags
            item={plate.item}
            locale={locale}
            iconsOnly
            placement="inline"
          />
        </div>
        {plate.item.description ? (
          <p className="ws-hl-hero__desc">{plate.item.description}</p>
        ) : null}
      </div>
    </article>
  );
}

/** One oversized lead plate + selectable chips that swap the featured dish. */
export function MenuHighlightHeroPlate({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const { sectionTitle, sectionSubtitle, dishes } = readHighlightMenuSection(
    block,
    locale,
  );
  const target = isActive ? activeEditTarget : null;
  const plates = dishes.slice(0, 4);
  const interactive = !editable && plates.length > 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const chipsRef = useRef<HTMLDivElement>(null);
  const chipsNavLabel = pickChrome('highlightHeroPlateNav', locale);
  const goToDishLabel = pickChrome('highlightHeroPlateGoTo', locale);

  useEffect(() => {
    if (activeIndex >= plates.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, plates.length]);

  const safeIndex =
    plates.length === 0 ? 0 : Math.min(activeIndex, plates.length - 1);
  const lead = plates[0];
  const supporting = plates.slice(1);
  const chipPlates = interactive ? plates : supporting;

  const focusChip = useCallback((index: number) => {
    const root = chipsRef.current;
    if (!root) return;
    const tab = root.querySelectorAll<HTMLElement>('[role="tab"]')[index];
    tab?.focus();
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (!interactive || index < 0 || index >= plates.length) {
        return;
      }
      setActiveIndex(index);
    },
    [interactive, plates.length],
  );

  const onChipsKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!interactive || plates.length < 2) {
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      let next = safeIndex + delta;
      if (next < 0) next = plates.length - 1;
      if (next >= plates.length) next = 0;
      setActiveIndex(next);
      focusChip(next);
    },
    [focusChip, interactive, plates.length, safeIndex],
  );

  return (
    <section
      className={['ws-hl-hero', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      data-ws-hl-hero={interactive ? '' : undefined}
      data-active={interactive ? String(safeIndex) : undefined}
      id="menu"
    >
      <div className="ws-hl-hero__inner">
        <header className="ws-hl-hero__intro">
          <span className="ws-hl-hero__accent" aria-hidden />
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-hl-hero__title"
          >
            {sectionTitle}
          </EditableContent>
          {sectionSubtitle || editable ? (
            <EditableContent
              as="p"
              fieldKey="sectionSubtitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-hl-hero__subtitle"
            >
              {sectionSubtitle}
            </EditableContent>
          ) : null}
        </header>

        {interactive ? (
          <div className="ws-hl-hero__stage">
            {plates.map((plate, index) => (
              <LeadPlate
                key={`${plate.columnIndex}-${plate.itemIndex}`}
                plate={plate}
                block={block}
                locale={locale}
                editable={editable}
                activeEditTarget={activeEditTarget}
                interactive
                plateIndex={index}
                selected={index === safeIndex}
                tabPanelId={`ws-hl-hero-tab-${block.blockId}-${index}`}
              />
            ))}
          </div>
        ) : lead ? (
          <LeadPlate
            plate={lead}
            block={block}
            locale={locale}
            editable={editable}
            activeEditTarget={activeEditTarget}
            interactive={false}
            plateIndex={0}
            selected
          />
        ) : null}

        {chipPlates.length > 0 ? (
          interactive ? (
            <div
              ref={chipsRef}
              className="ws-hl-hero__chips"
              role="tablist"
              aria-label={chipsNavLabel || sectionTitle || 'Highlights'}
            >
              {chipPlates.map(({ item, columnIndex, itemIndex }, index) => {
                const selected = index === safeIndex;
                return (
                  <button
                    key={`${columnIndex}-${itemIndex}`}
                    type="button"
                    role="tab"
                    id={`ws-hl-hero-tab-${block.blockId}-${index}`}
                    className="ws-hl-hero__chip"
                    aria-selected={selected}
                    aria-label={
                      item.name?.trim()
                        ? item.name
                        : goToDishLabel.replace('{n}', String(index + 1))
                    }
                    tabIndex={selected ? 0 : -1}
                    onClick={() => goTo(index)}
                    onKeyDown={onChipsKeyDown}
                  >
                    <span className="ws-hl-hero__chip-name">{item.name}</span>
                    {item.description ? (
                      <span className="ws-hl-hero__chip-desc">{item.description}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <ul className="ws-hl-hero__chips" aria-label={sectionTitle || 'Also today'}>
              {chipPlates.map(({ item, columnIndex, itemIndex }) => (
                <li
                  key={`${columnIndex}-${itemIndex}`}
                  className={[
                    'ws-hl-hero__chip',
                    isMenuItemActive(
                      target?.kind === 'menu-item' ? target : null,
                      columnIndex,
                      itemIndex,
                    )
                      ? 'ws-edit-child--active'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  {...(editable
                    ? {
                        'data-edit-child': 'menu-item' as const,
                        'data-column-index': columnIndex,
                        'data-item-index': itemIndex,
                      }
                    : {})}
                >
                  <span className="ws-hl-hero__chip-name">{item.name}</span>
                  {item.description ? (
                    <span className="ws-hl-hero__chip-desc">{item.description}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>
    </section>
  );
}
